import Link from "next/link";
import { c } from "@/lib/vster/theme";
import Icon from "@/components/vster/Icon";
import type { PublicProgram } from "@/lib/vster/programs";

// 공고 카드 (목록·홈·워크스페이스 공용) — 부스터 디자인(nextjs_design 이식) + 실데이터(PublicProgram).
//   - 카드 전체가 상세 링크. 찜 버튼은 saveSlot 으로 주입(클라이언트 SaveProgramButton) → 우상단 오버레이.
//   - 칩 순서(고정): 사업도메인(잉크) → 지원사업분류(cat2,중립) → 대표키워드(파랑) → 마감(램프).
export default function ProgramCard({ p, saveSlot }: { p: PublicProgram; saveSlot?: React.ReactNode }) {
  // AI 3줄 요약 — LLM summary3Lines 우선, 없으면 구조화 필드 조립(lines3)으로 폴백.
  const llm3 = (p.summary3Lines ?? "")
    .split("\n")
    // 선행 리스트마커만 제거: 불릿(- • · 등) 또는 '1.'/'2)' 같은 한자리~두자리 순번(+공백).
    //   날짜/금액 등 콘텐츠 숫자(예: "5/20~", "2026.5.26.", "500만원")는 지우지 않는다.
    .map((l) => l.replace(/^\s*(?:[-–—•·‣▪*]\s+|\d{1,2}[.)]\s+)/, "").trim())
    .filter(Boolean)
    .slice(0, 3);
  // 폴백 요약(하이브리드) — LLM summary3Lines 가 없을 때만 구조화 필드로 '누가/얼마/언제까지' 3줄 조립.
  //   누가=지원대상(lines3), 얼마=지원금액(상세참조면 지원내용으로 폴백), 언제까지=마감일(없으면 상시).
  const clip = (s: string, max = 96) => (s.length > max ? s.slice(0, max).replace(/[\s,·]+$/, "") + "…" : s);
  const fb = (() => {
    if (llm3.length > 0) return [] as { k: string; v: string }[];
    const who = p.lines3.find((l) => l.k === "지원대상")?.v;
    const amt = p.amount && p.amount !== "상세 참조" ? clip(p.amount) : p.lines3.find((l) => l.k === "지원내용")?.v;
    const when = p.deadline ? `~ ${p.deadline}` : p.dday === "상시" ? "상시 모집" : null;
    const rows: { k: string; v: string }[] = [];
    if (who) rows.push({ k: "누가", v: who });
    if (amt) rows.push({ k: "얼마", v: amt });
    if (when) rows.push({ k: "언제까지", v: when });
    return rows;
  })();
  const keywords = (p.tags ?? []).slice(0, 3); // 대표키워드
  // 소관부처 · 사업수행기관(직접수행) · 지역 — 같은 값이면 중복 제거.
  const orgRegion = [...new Set([p.governing, p.executor, p.region].filter(Boolean))];
  const chipBase = { fontSize: 12, fontWeight: 600, letterSpacing: ".04em", padding: "4px 10px", borderRadius: 6 } as const;

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <Link
        href={`/programs/${p.id}` as never}
        className="card-hover"
        style={{
          position: "relative", overflow: "hidden", background: c.surface,
          border: `1px solid ${c.line}`, borderRadius: 16, padding: "20px 22px 20px 28px",
          boxShadow: "0 4px 16px -14px rgba(14,23,38,.14)", display: "flex", flexDirection: "column",
          gap: 10, textDecoration: "none", height: "100%", opacity: p.closed ? 0.64 : 1,
        }}
      >
        {/* 좌측 상태 레일 */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: p.dotColor }} />

        {/* 칩(고정 순서): 사업도메인 → 지원사업분류(cat2) → 대표키워드 → 마감 */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingRight: 44 }}>
          {p.domainLabel && <span style={{ ...chipBase, background: c.ink, color: "#fff" }}>{p.domainLabel}</span>}
          {p.cat2 && <span style={{ ...chipBase, background: c.surfaceAlt, color: c.muted }}>{p.cat2}</span>}
          {keywords.map((k) => (
            <span key={k} style={{ ...chipBase, background: c.actionSoft, color: c.actionDark }}>{k}</span>
          ))}
          <span style={{ ...chipBase, background: p.ddBg, color: p.ddColor }}>{p.dday}</span>
        </div>

        <h3 style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.35, margin: 0, color: c.ink }}>{p.title}</h3>

        <div className="num" style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>
          {orgRegion.join(" · ")}
          {p.deadline && <>{orgRegion.length ? " · " : ""}마감일 {p.deadline}</>}
          {(orgRegion.length || p.deadline) ? " · " : ""}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, verticalAlign: "middle" }} title="조회수">
            <Icon name="visibility" size={15} />{(p.viewCount ?? 0).toLocaleString()}
          </span>
        </div>

        {/* 요약 박스 — ① LLM summary3Lines(서술형 불릿) 우선, ② 없으면 구조화 '누가/얼마/언제까지' 폴백. */}
        {llm3.length > 0 ? (
          <div style={{ background: c.surfaceAlt, borderRadius: 8, padding: "12px 14px", marginTop: "auto", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1 }}>
              {llm3.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{ flexShrink: 0, width: 5, height: 5, borderRadius: "50%", background: c.actionDark, marginTop: 7 }} />
                  <span style={{ fontSize: 13, lineHeight: 1.5, color: c.ink }}>{l}</span>
                </div>
              ))}
            </div>
            <Icon name="auto_awesome" size={16} color={c.actionDark} fill={1} style={{ flexShrink: 0, marginTop: 1 }} />
          </div>
        ) : fb.length > 0 ? (
          <div style={{ background: c.surfaceAlt, borderRadius: 8, padding: "12px 14px", marginTop: "auto", display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            {fb.map((r) => (
              <div key={r.k} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5 }}>
                <span style={{ flexShrink: 0, minWidth: 48, fontWeight: 700, color: c.actionDark }}>{r.k}</span>
                <span style={{ minWidth: 0, color: c.ink }}>{r.v}</span>
              </div>
            ))}
          </div>
        ) : null}
      </Link>
      {saveSlot && <div style={{ position: "absolute", top: 14, right: 16, zIndex: 2 }}>{saveSlot}</div>}
    </div>
  );
}
